import { Link } from 'react-router'
import { Building2, Plus } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useOpsTenants } from '@/hooks/useOpsTenants'
import { TenantStatusBadge } from '@/components/tenants/TenantStatusBadge'

/** Every tenant, for staff: status, who has access, and whether anyone owns it yet. ADMIN onboards new ones. */
export function OpsTenantsPage() {
  const { user } = useAuth()
  const query = useOpsTenants()
  const isAdmin = user?.role === 'ADMIN'

  const newTenantButton = (
    <Button asChild>
      <Link to="/ops/tenants/new">
        <Plus />
        New tenant
      </Link>
    </Button>
  )

  function renderBody() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) {
      return <ErrorState message={apiErrorMessage(query.error, 'Could not load tenants.')} />
    }
    if (query.data.tenants.length === 0) {
      return (
        <EmptyState
          icon={Building2}
          title="No tenants yet"
          {...(isAdmin ? { action: newTenantButton } : {})}
        />
      )
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Members</TableHead>
            <TableHead>Owners</TableHead>
            <TableHead className="text-right">Pending invites</TableHead>
            <TableHead className="text-right">Keys (live / test)</TableHead>
            <TableHead>Last API use</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.data.tenants.map((tenant) => (
            <TableRow key={tenant.id}>
              <TableCell>
                <Link to={`/ops/tenants/${tenant.id}`} className="font-medium text-link hover:underline">
                  {tenant.name}
                </Link>
              </TableCell>
              <TableCell className="font-mono text-caption text-muted-foreground">{tenant.slug}</TableCell>
              <TableCell>
                <TenantStatusBadge status={tenant.status} />
              </TableCell>
              <TableCell className="text-right">{tenant.memberCount}</TableCell>
              <TableCell>
                {tenant.ownerCount === 0 ? <Badge tone="warning">No owner</Badge> : tenant.ownerCount}
              </TableCell>
              <TableCell className="text-right">{tenant.pendingInvitationCount}</TableCell>
              <TableCell className="text-right">
                {tenant.keyCounts.live} / {tenant.keyCounts.test}
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {tenant.lastApiUseAt ? new Date(tenant.lastApiUseAt).toLocaleString() : 'Never'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <>
      <PageHeader
        title="Tenants"
        description={query.data ? `${String(query.data.tenants.length)} organizations` : undefined}
        actions={isAdmin ? newTenantButton : undefined}
      />
      {renderBody()}
    </>
  )
}
